import { useEventBus } from '@vueuse/core'
import type { EventBusKey } from '@vueuse/core'

export type ScanResult = {
    scanResultStatus: string
    isMalicious: boolean
    objectKey?: string
    partyId?: string
    workflowId?: string
    workspaceId?: string
}

export const UPLOAD_SCANNED: EventBusKey<ScanResult> = Symbol('upload-scanned')

const UPLOAD_SCANS_UNAVAILABLE: EventBusKey<string> = Symbol('upload-scans-unavailable')

const UPLOAD_SCANS_MISSED: EventBusKey<void> = Symbol('upload-scans-missed')

let unavailableReason: string | undefined

export const reportUploadScansUnavailable = (reason: string | undefined) => {
    unavailableReason = reason

    if (reason) {
        useEventBus(UPLOAD_SCANS_UNAVAILABLE).emit(reason)
    }
}

const keyedScans = new Map<string, ScanResult>()
let keepsKeyedScans = false

const keepKeyedScans = () => {
    if (keepsKeyedScans) {
        return
    }

    keepsKeyedScans = true
    useEventBus(UPLOAD_SCANNED).on((scan) => {
        if (scan.objectKey) {
            keyedScans.set(scan.objectKey, scan)
        }
    })
}

export const resetUploadScans = () => {
    unavailableReason = undefined
    keyedScans.clear()
}

export const reportUploadScansMissed = () => useEventBus(UPLOAD_SCANS_MISSED).emit()

export const watchUploadScans = () => {
    keepKeyedScans()

    const scans: ScanResult[] = []
    let missed = false
    let settle = () => {}

    const offScanned = useEventBus(UPLOAD_SCANNED).on((scan) => {
        scans.push(scan)
        settle()
    })
    const offUnavailable = useEventBus(UPLOAD_SCANS_UNAVAILABLE).on(() => settle())
    const offMissed = useEventBus(UPLOAD_SCANS_MISSED).on(() => {
        missed = true
        settle()
    })

    const stop = () => {
        offScanned()
        offUnavailable()
        offMissed()
    }

    const verdictsFor = (objectKeys: string[]) => new Promise<ScanResult[]>((resolve, reject) => {
        settle = () => {
            if (unavailableReason) {
                stop()
                reject(new Error(unavailableReason))
                return
            }

            const verdicts = objectKeys.map(key => keyedScans.get(key) ?? scans.find(scan => !scan.objectKey))

            if (missed || verdicts.every(Boolean)) {
                stop()
                resolve(verdicts.filter((verdict): verdict is ScanResult => !!verdict))
            }
        }

        settle()
    })

    return { verdictsFor, stop }
}
